import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import BellCurveChart from "../band-distribution/BellCurveChart";
import BandingSummaryCard from "./BandingSummaryCard";
import BandingSimulationPanel from "./BandingSimulationPanel";
import FinalBandingTable from "./FinalBandingTable";
import FinalBandingTableManageTeam from "./FinalBandingTableManageTeam";
import PersonalGoalsContent from "./PersonalGoalsContent";
import BandDisagreementDialog from "../BandDisagreementDialog";
import ClosePerformanceCycleSection from "./ClosePerformanceCycleSection";
import { useUnitMembersData } from "./hooks/useUnitMembersData";
import { usePerformanceBandsSupervisorSync } from "./hooks/usePerformanceBandsSupervisorSync";
import { useBandingSimulation } from "./hooks/useBandingSimulation";
import { TeamMemberData, BandPercentages, UnitMemberData } from "./types";
import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

interface SupervisorTabContentProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  updatedTeamMembersData: TeamMemberData[];
  bandStats: any;
  bandPercentages: BandPercentages;
  handlePercentageChange: (band: keyof BandPercentages, value: number[]) => void;
  handlePercentageCommit: (band: keyof BandPercentages, value: number[]) => void;
  resetBoundaries: () => void;
  currentUser: any;
  bandBoundaries: any;
  handleBoundariesChange: (boundaries: any) => void;
  bellCurveRefreshTrigger: number;
  isSavingAll: boolean;
  handleSave: () => void;
  handleRowSave: (member: TeamMemberData) => void;
  handleRowEdit: (memberId: string) => void;
  handleCommentsChange: (memberId: string, comments: string) => void;
  isReleasedBanding: boolean;
  userPerformanceBand: any;
  onAccept: () => void;
  onDisagree: () => void;
}

const SupervisorTabContent = ({
  activeTab,
  setActiveTab,
  updatedTeamMembersData,
  bandStats,
  bandPercentages,
  handlePercentageChange,
  handlePercentageCommit,
  resetBoundaries,
  currentUser,
  bandBoundaries,
  handleBoundariesChange,
  bellCurveRefreshTrigger,
  isSavingAll,
  handleSave,
  handleRowSave,
  handleRowEdit,
  handleCommentsChange,
  isReleasedBanding,
  userPerformanceBand,
  onAccept,
  onDisagree
}: SupervisorTabContentProps) => {
  
  const { activeYear } = usePerformanceYear();
  
  console.log('SupervisorTabContent: Rendering with currentUser:', currentUser);
  console.log('SupervisorTabContent: currentUser.is_unit_head value:', currentUser?.is_unit_head);
  console.log('SupervisorTabContent: currentUser.is_unit_head type:', typeof currentUser?.is_unit_head);
  
  // Check if user is a unit head - STRICT boolean check
  const isUnitHead = currentUser && currentUser.is_unit_head === true;
  
  console.log('SupervisorTabContent: Final isUnitHead determination:', isUnitHead);
  console.log('SupervisorTabContent: Should show Manage Unit tab:', isUnitHead);

  const [isSavingUnitBanding, setIsSavingUnitBanding] = useState(false);
  const [unitBandPercentages, setUnitBandPercentages] = useState<BandPercentages>({
    A: 10,
    B: 25,
    C: 55,
    D: 10
  });
  const [unitBandBoundaries, setUnitBandBoundaries] = useState({
    line1: 0.5,
    line2: 3.0,
    line3: 4.25,
    line4: 5.0
  });

  // Use banding simulation hook for unit tab
  const { convertPercentagesToBoundaries, resetBandingSimulation } = useBandingSimulation(currentUser);

  // Sync supervisor information in performance_bands table for team members
  usePerformanceBandsSupervisorSync(updatedTeamMembersData, currentUser, activeYear);

  // Fetch unit members data only if user is a unit head
  const { unitMembersData, isLoading: isUnitLoading, updateUnitMembersWithBoundaries, setUnitMembersData, refreshStatuses } = useUnitMembersData(currentUser, isUnitHead);

  console.log('SupervisorTabContent: Unit members data:', unitMembersData);
  console.log('SupervisorTabContent: Unit members loading:', isUnitLoading);

  // Calculate dynamic boundaries based on actual unit member rating ranges
  const calculateDynamicBoundaries = useMemo(() => {
    return (percentages: BandPercentages, memberData: UnitMemberData[]) => {
      // Get all valid manager ratings
      const managerRatings = memberData
        .map(member => member.manager_rating || member.averageManagerRating)
        .filter(rating => rating > 0)
        .sort((a, b) => a - b);

      if (managerRatings.length === 0) {
        // Fallback to default boundaries if no ratings
        return {
          line1: 2.5,
          line2: 3.0,
          line3: 3.5,
          line4: 5.0
        };
      }

      const minRating = managerRatings[0];
      const maxRating = managerRatings[managerRatings.length - 1];
      const range = maxRating - minRating;

      // If all ratings are the same, use small offsets
      if (range === 0) {
        return {
          line1: minRating + 0.1,
          line2: minRating + 0.2,
          line3: minRating + 0.3,
          line4: maxRating
        };
      }

      // Calculate boundaries based on percentages
      // D band: 0% to D%
      // C band: D% to (D+C)%
      // B band: (D+C)% to (D+C+B)%
      // A band: (D+C+B)% to 100%
      
      const cumulativeD = percentages.D;
      const cumulativeC = cumulativeD + percentages.C;
      const cumulativeB = cumulativeC + percentages.B;
      
      // Convert percentages to actual rating boundaries
      const line1 = minRating + (cumulativeD / 100) * range; // D to C boundary
      const line2 = minRating + (cumulativeC / 100) * range; // C to B boundary
      const line3 = minRating + (cumulativeB / 100) * range; // B to A boundary
      const line4 = maxRating; // A boundary (max rating)

      return {
        line1: Math.round(line1 * 100) / 100,
        line2: Math.round(line2 * 100) / 100,
        line3: Math.round(line3 * 100) / 100,
        line4: Math.round(line4 * 100) / 100
      };
    };
  }, []);

  // Update unit boundaries when percentages or unit members change
  useEffect(() => {
    if (isUnitHead && unitMembersData.length > 0) {
      const newBoundaries = calculateDynamicBoundaries(unitBandPercentages, unitMembersData);
      setUnitBandBoundaries(newBoundaries);
      console.log('Dynamic unit boundaries calculated:', newBoundaries);
    }
  }, [unitBandPercentages, unitMembersData, isUnitHead, calculateDynamicBoundaries]);

  // Update unit members when boundaries change
  useEffect(() => {
    if (isUnitHead && unitMembersData.length > 0 && updateUnitMembersWithBoundaries) {
      updateUnitMembersWithBoundaries(unitBandBoundaries);
    }
  }, [unitBandBoundaries, isUnitHead, unitMembersData.length, updateUnitMembersWithBoundaries]);

  // Handle percentage changes for unit tab
  const handleUnitPercentageChange = (band: keyof BandPercentages, value: number[]) => {
    const newPercentages = {
      ...unitBandPercentages,
      [band]: value[0]
    };
    setUnitBandPercentages(newPercentages);
    console.log('Unit percentages changed:', newPercentages);
  };

  const handleUnitPercentageCommit = (band: keyof BandPercentages, value: number[]) => {
    console.log(`Unit Band ${band} committed with value: ${value[0]}`);
  };

  // Handle unit boundaries change from bell curve (manual dragging)
  const handleUnitBoundariesChange = (boundaries: any) => {
    const safeBoundaries = {
      ...boundaries,
      line4: boundaries.line4 || 5.0
    };
    setUnitBandBoundaries(safeBoundaries);
    console.log('Unit boundaries changed from bell curve:', safeBoundaries);
  };

  // Reset unit boundaries
  const resetUnitBoundaries = async () => {
    const defaultPercentages = { A: 10, B: 25, C: 55, D: 10 };
    setUnitBandPercentages(defaultPercentages);
    
    // Calculate new boundaries based on actual data range
    if (unitMembersData.length > 0) {
      const newBoundaries = calculateDynamicBoundaries(defaultPercentages, unitMembersData);
      setUnitBandBoundaries(newBoundaries);
    }
    
    // Save to database
    await resetBandingSimulation();
    
    console.log('Unit boundaries reset to default');
    toast.success('Unit banding simulation reset to default values');
  };

  // Handle Save Banding for unit members - ONLY UPDATE existing records, never create new ones
  const handleUnitSaveBanding = async () => {
    if (!currentUser || !isUnitHead || isSavingUnitBanding) return;

    setIsSavingUnitBanding(true);
    try {
      console.log('Starting unit banding save for unit head:', currentUser.id);

      // Filter members with valid adjusted banding AND existing records
      const membersToSave = unitMembersData.filter(member => 
        member.finalBanding && 
        member.finalBanding !== '-' && 
        member.hasSavedRecord // Only update members that have existing records
      );

      if (membersToSave.length === 0) {
        toast.error("No existing performance band records to update");
        return;
      }

      console.log('Members with existing records to update:', membersToSave.length);

      // Only update existing records - use UPDATE, not UPSERT
      const updatePromises = membersToSave.map(async (member) => {
        console.log(`Updating existing performance band record for ${member.full_name}`);

        // Use UPDATE to only modify existing records
        const { error } = await supabase
          .from('performance_bands')
          .update({
            final_banding: member.finalBanding,
            status: 'Final Banding Done'
          })
          .eq('employee_id', member.id)
          .eq('year', activeYear);

        if (error) {
          console.error(`Error updating performance band for ${member.full_name}:`, error);
          throw error;
        }

        return member;
      });

      await Promise.all(updatePromises);

      // Update local state to reflect saved status
      setUnitMembersData(prev => prev.map(member => ({
        ...member,
        hasSavedRecord: true
      })));

      toast.success(`Unit banding updated successfully for ${membersToSave.length} existing records!`);
      console.log('Unit banding update completed successfully');

    } catch (error) {
      console.error('Error updating unit banding:', error);
      toast.error('Error updating unit banding');
    } finally {
      setIsSavingUnitBanding(false);
    }
  };

  // Calculate band statistics for unit members
  const getUnitBandStatistics = () => {
    if (!unitMembersData || unitMembersData.length === 0) {
      console.log('SupervisorTabContent: No unit members data, returning empty stats');
      return {
        A: { count: 0, percentage: 0 },
        B: { count: 0, percentage: 0 },
        C: { count: 0, percentage: 0 },
        D: { count: 0, percentage: 0 }
      };
    }

    // Calculate based on finalBanding (adjusted banding) not allocatedPerformanceBand (system banding)
    const bandCounts = unitMembersData.reduce((acc, member) => {
      const band = member.finalBanding; // Use adjusted banding for statistics
      if (band === 'A') acc.bandA++;
      else if (band === 'B') acc.bandB++;
      else if (band === 'C') acc.bandC++;
      else if (band === 'D') acc.bandD++;
      return acc;
    }, { bandA: 0, bandB: 0, bandC: 0, bandD: 0 });

    const totalMembers = unitMembersData.length;

    const stats = {
      A: { 
        count: bandCounts.bandA, 
        percentage: totalMembers > 0 ? Math.round((bandCounts.bandA / totalMembers) * 100) : 0 
      },
      B: { 
        count: bandCounts.bandB, 
        percentage: totalMembers > 0 ? Math.round((bandCounts.bandB / totalMembers) * 100) : 0 
      },
      C: { 
        count: bandCounts.bandC, 
        percentage: totalMembers > 0 ? Math.round((bandCounts.bandC / totalMembers) * 100) : 0 
      },
      D: { 
        count: bandCounts.bandD, 
        percentage: totalMembers > 0 ? Math.round((bandCounts.bandD / totalMembers) * 100) : 0 
      }
    };

    console.log('SupervisorTabContent: Unit band statistics:', stats);
    return stats;
  };

  const unitBandStats = getUnitBandStatistics();

  return (
    <div className="bg-secondary/30 rounded-lg">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex border-b border-primary/20">
          <TabsList className="bg-transparent">
            <TabsTrigger
              value="personal-goals"
              className="px-6 py-3 text-sm font-medium transition-colors data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=inactive]:text-gray-400 data-[state=inactive]:hover:text-white"
            >
              Personal Goals
            </TabsTrigger>
            <TabsTrigger
              value="manage-team-members"
              className="px-6 py-3 text-sm font-medium transition-colors data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=inactive]:text-gray-400 data-[state=inactive]:hover:text-white"
            >
              Manage Team Members
            </TabsTrigger>
            {isUnitHead && (
              <TabsTrigger
                value="manage-unit"
                className="px-6 py-3 text-sm font-medium transition-colors data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=inactive]:text-gray-400 data-[state=inactive]:hover:text-white"
              >
                Manage Unit
              </TabsTrigger>
            )}
          </TabsList>
        </div>
        
        <TabsContent value="personal-goals" className="mt-4">
          <PersonalGoalsContent
            userPerformanceBand={userPerformanceBand}
            onAccept={onAccept}
            onDisagree={onDisagree}
          />
        </TabsContent>
        
        <TabsContent value="manage-team-members" className="mt-4">
          <div className="space-y-6">
            {updatedTeamMembersData.length > 0 && (
              <>
                {/* For Manage Team Members tab, hide all sections regardless of unit head status */}
                {/* Use the simplified table component for manage team members */}
                <FinalBandingTableManageTeam
                  teamMembersData={updatedTeamMembersData}
                  onRowSave={handleRowSave}
                  onRowEdit={handleRowEdit}
                  onCommentsChange={handleCommentsChange}
                  isReleasedBanding={isReleasedBanding}
                  isUnitHead={false}
                  currentUser={currentUser}
                />
              </>
            )}
          </div>
        </TabsContent>
        
        {isUnitHead && (
          <TabsContent value="manage-unit" className="mt-4">
            <div className="space-y-6">
              {isUnitLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-primary animate-pulse">Loading unit members data...</div>
                </div>
              ) : unitMembersData.length > 0 ? (
                <>
                  <ClosePerformanceCycleSection 
                    unitMembersData={unitMembersData}
                    bandingYear={new Date().getFullYear()}
                  />
                  
                  <div className="flex gap-6 h-[500px]">
                    <div className="w-1/2 flex flex-col">
                      <div className="h-1/2 mb-3">
                        <BandingSummaryCard bandStats={unitBandStats} />
                      </div>
                      <div className="h-1/2">
                        <BandingSimulationPanel 
                          bandPercentages={unitBandPercentages}
                          onPercentageChange={handleUnitPercentageChange}
                          onPercentageCommit={handleUnitPercentageCommit}
                          onReset={resetUnitBoundaries}
                          currentUser={currentUser}
                        />
                      </div>
                    </div>

                    <Card className="bg-secondary/50 border border-primary flex flex-col w-[85%] h-[500px]">
                      <CardHeader className="flex-shrink-0 pb-1 my-0 py-2">
                        <div className="flex justify-between items-center py-0 my-0">
                          <div>
                            <CardTitle className="text-white">Unit Band Distribution</CardTitle>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="flex-1 flex flex-col pt-2 pb-2 py-0">
                        <div className="flex-1">
                          <BellCurveChart 
                            data={unitMembersData.map(member => member.manager_rating || member.averageManagerRating).filter(rating => rating >= 1)} 
                            boundaries={unitBandBoundaries} 
                            onBoundariesChange={handleUnitBoundariesChange} 
                            onReset={resetUnitBoundaries} 
                            refreshTrigger={Math.random()} 
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <FinalBandingTable
                    teamMembersData={unitMembersData}
                    isSavingAll={isSavingUnitBanding}
                    onSaveAll={handleUnitSaveBanding}
                    onRowSave={() => {}}
                    onRowEdit={() => {}}
                    onCommentsChange={() => {}}
                    isReleasedBanding={false}
                    isUnitHead={isUnitHead}
                    isManageUnitTab={true}
                    onRefreshStatuses={refreshStatuses}
                  />
                </>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <p className="text-gray-400">No unit members found or no performance data available.</p>
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default SupervisorTabContent;
